import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import { type DrawEvent } from "@typings/components/map";
import "mapbox-gl/dist/mapbox-gl.css";
import { config } from "@/config";
import CircularProgress from "@mui/material/CircularProgress";
import { applyGeoJsonLayer } from "./MapHelpers";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import Button from "@commons/Button";
import Text from "@commons/Typography/Text";
import Heading from "@commons/Typography/Heading";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SatelliteAltSharpIcon from "@mui/icons-material/SatelliteAltSharp";
import { usePage } from "@hooks/usePage";

mapboxgl.accessToken = config.mapbox.accessToken;

type MapDisplayProps = {
  longitude: number;
  latitude: number;
  zoom?: number;
  style?: string;
  draw?: boolean;
};

function MapLocalization({
  longitude = config.map?.longitude as number,
  latitude = config.map?.latitude as number,
  zoom = config.map?.zoom as number,
  style = config.map?.style as string,
  draw = false,
}: MapDisplayProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const drawRef = useRef<MapboxDraw | null>(null);
  const [newDrawPoint, setNewDrawPoint] = useState<{
    id: string;
    coordinates: [number, number];
  } | null>(null);
  const [isSatellite, setIsSatellite] = useState<boolean>(false);
  const { setDataModal, areaMap } = usePage();
  const areaMapRef = useRef(areaMap);

  useEffect(() => {
    areaMapRef.current = areaMap;
  }, [areaMap]);

  const handleDrawPoint = useCallback((event: DrawEvent) => {
    const draw = drawRef.current;
    if (!draw) return;
    if (!event?.features?.length) return;

    const pointsInEvent = event.features.filter(
      (point) => point.geometry?.type === "Point",
    );
    if (pointsInEvent.length === 0) return;

    const lastPoint = pointsInEvent[pointsInEvent.length - 1];
    const allDrawPoints = draw.getAll()?.features ?? [];
    allDrawPoints.forEach((point) => {
      if (point.id !== lastPoint.id && draw.get(point.id as string)) {
        draw.delete(point.id as string);
      }
    });

    setNewDrawPoint({
      id: lastPoint.id,
      coordinates: lastPoint.geometry.coordinates as [number, number],
    });
  }, []);

  function handleCloseDrawPoint() {
    if (newDrawPoint) {
      drawRef.current?.delete(newDrawPoint.id);
      setNewDrawPoint(null);
    }
  }

  const toggleMapStyle = useCallback(() => {
    if (!mapRef.current) return;
    if (drawRef.current) mapRef.current.removeControl(drawRef.current);
    const newStyle = isSatellite
      ? config.map?.style
      : config.map?.styleSatellite;
    mapRef.current.setStyle(newStyle || style);
    setIsSatellite(!isSatellite);
  }, [isSatellite, style]);

  useEffect(() => {
    if (!mapRef.current) return;

    const handleStyleData = () => {
      if (!mapRef.current) return;

      if (areaMapRef.current) {
        applyGeoJsonLayer(mapRef.current, areaMapRef.current);
      }

      if (markerRef.current) {
        markerRef.current.addTo(mapRef.current);
      }

      if (drawRef.current && !mapRef.current.hasControl(drawRef.current)) {
        mapRef.current.addControl(drawRef.current);
      }
    };

    mapRef.current.on("styledata", handleStyleData);

    return () => {
      mapRef.current?.off("styledata", handleStyleData);
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style,
      center: [longitude, latitude],
      zoom,
    });

    markerRef.current = new mapboxgl.Marker({ color: "#0000ff" })
      .setLngLat([longitude, latitude])
      .addTo(mapRef.current);

    mapRef.current.on("load", () => {
      setMapLoaded(true);
      if (mapRef.current && draw) {
        drawRef.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: { point: true },
        });
        mapRef.current.addControl(drawRef.current);
        mapRef.current.on("draw.create", handleDrawPoint);
        mapRef.current.on("draw.update", handleDrawPoint);
      }
      if (mapRef.current && areaMap) {
        applyGeoJsonLayer(mapRef.current, areaMap);
      }
    });

    return () => {
      if (mapRef.current && drawRef.current) {
        mapRef.current.off("draw.create", handleDrawPoint);
        mapRef.current.off("draw.update", handleDrawPoint);
        mapRef.current.removeControl(drawRef.current);
      }

      markerRef.current?.remove();

      setMapLoaded(false);
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
      drawRef.current = null;
    };
  }, [longitude, latitude, handleDrawPoint, zoom, style, draw, areaMap]);

  useEffect(() => {
    if (!mapRef.current || !areaMap) return;
    const applyArea = () => applyGeoJsonLayer(mapRef.current!, areaMap);
    if (mapLoaded) applyArea();
    mapRef.current.on("styledata", applyArea);
    return () => {
      mapRef.current?.off("styledata", applyArea);
    };
  }, [areaMap, mapLoaded]);

  return (
    <div data-testid="map-container" className="w-full h-full relative">
      <div ref={mapContainerRef} className="w-full h-full">
        <div
          className={`absolute right-2 z-10 ${draw ? "top-12" : "top-2"} transition-all`}
        >
          <IconButton
            onClick={toggleMapStyle}
            className="bg-white shadow-lg hover:bg-gray-100"
            aria-label="Alternar visão satélite"
          >
            <SatelliteAltSharpIcon
              className={`${isSatellite ? "text-blue-500" : "text-gray-600"} text-[18px]`}
            />
          </IconButton>
        </div>
        {newDrawPoint && (
          <div className="absolute right-0 bottom-0 p-4 bg-white bg-opacity-90 rounded shadow min-w-48 z-10">
            <div className="flex items-center justify-between mb-2">
              <Heading size="sm">Coordenadas:</Heading>
              <IconButton className="w-4 h-4" onClick={handleCloseDrawPoint}>
                <CloseIcon className="w-4 h-4" />
              </IconButton>
            </div>
            <div className="mb-4">
              <Text className="font-roboto">
                Lat: {newDrawPoint.coordinates[1].toFixed(8)}
              </Text>
              <Text className="font-roboto">
                Lng: {newDrawPoint.coordinates[0].toFixed(8)}
              </Text>
            </div>
            <Button
              customVariant="secondary"
              size="full"
              onClick={() => {
                setDataModal({
                  latitude: newDrawPoint.coordinates[1].toFixed(8),
                  longitude: newDrawPoint.coordinates[0].toFixed(8),
                });
                handleCloseDrawPoint();
              }}
              startIcon={<ContentCopyIcon />}
            >
              <Text size="sm">Copiar valores</Text>
            </Button>
          </div>
        )}
      </div>
      {!mapLoaded && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-white bg-opacity-70 z-10">
          <CircularProgress className="text-primary" />
        </div>
      )}
    </div>
  );
}

export default React.memo(MapLocalization) as typeof MapLocalization;
